import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useDataProvider } from 'react-admin';
import { useState, useEffect } from 'react';
import { Truck } from '../../models/common';
import { Cargo } from '../../models/Cargo';
// 添加 JSZip 导入
// 需要先安装jszip依赖: npm install --save @types/jszip jszip
import JSZip from 'jszip';
import { format } from 'date-fns';
// 添加新的图标导入
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';

interface WaybillImagesDialogProps {
  open: boolean;
  onClose: () => void;
  waybillId: string;
  truck: Truck | null;
  cargo: Cargo | null;
  receiptImage: string | null;
}

// 添加图片预览状态接口
interface ImagePreviewState {
  src: string | null;
  rotation: number;
  scale: number;
}

const ImageSection = ({ title, images }: { title: string; images: string[] }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {images.map((image, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Box
            sx={{
              width: '100%',
              height: 200,
              position: 'relative',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                border: '1px solid #eee',
                borderRadius: 1,
              },
            }}
          >
            {/* <img src={image.url} alt={image.title} />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                textAlign: 'center',
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                py: 0.5,
              }}
            >
              {image.title}
            </Typography> */}
          </Box>
        </Grid>
      ))}
    </Grid>
  </Box>
);
// 车牌-货物-日期-订单ID
const WaybillImagesDialog: React.FC<WaybillImagesDialogProps> = ({
  open,
  onClose,
  waybillId,
  truck,
  cargo,
  receiptImage,
}) => {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [idCardFrontImage, setIdCardFrontImage] = useState<string>();
  const [idCardBackImage, setIdCardBackImage] = useState<string>();
  const [drivingLicenseImage, setDrivingLicenseImage] = useState<string>();
  const [certificate, setCertificate] = useState<string>();
  const [truckDriveLicense, setTruckDriveLicense] = useState<string>();
  const [truckLicense, setTruckLicense] = useState<string>();
  // 添加预览状态
  const [preview, setPreview] = useState<ImagePreviewState>({
    src: null,
    rotation: 0,
    scale: 1,
  });

  // 添加图片控制函数
  const handleRotateLeft = () => {
    setPreview(prev => ({
      ...prev,
      rotation: (prev.rotation - 90) % 360,
    }));
  };

  const handleRotateRight = () => {
    setPreview(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const handleZoomIn = () => {
    setPreview(prev => ({
      ...prev,
      scale: Math.min(prev.scale + 0.5, 3),
    }));
  };

  const handleZoomOut = () => {
    setPreview(prev => ({
      ...prev,
      scale: Math.max(prev.scale - 0.5, 0.5),
    }));
  };

  const handleClosePreview = () => {
    setPreview({ src: null, rotation: 0, scale: 1 });
  };

  useEffect(() => {
    const fetchImages = async () => {
      if (!open) return;

      try {
        setLoading(true);
        // const waybillImages = await dataProvider.getWaybillImages(waybillId);
        // setWeightImages(waybillImages.map(img => ({
        //   title: img.title || '磅单',
        //   url: img.url
        // })));

        const imagesData = await dataProvider.getVerifyImages(truck?.owner ?? "");
        const face = await dataProvider.getVerifyImageData(imagesData.face);
        const emblem = await dataProvider.getVerifyImageData(imagesData.emblem);
        const driverLicense = await dataProvider.getVerifyImageData(imagesData.driver_license);
        const certificate = await dataProvider.getVerifyImageData(imagesData.certificate);
        setIdCardFrontImage(face);
        setIdCardBackImage(emblem);
        setDrivingLicenseImage(driverLicense);
        setCertificate(certificate);

        const vehicleImages = await dataProvider.getTruckVerifyImages(truck?._id ?? "");
        const truckLicense = await dataProvider.getVerifyImageData(vehicleImages.truck_license);
        const trcukDriveLicenseImage = await dataProvider.getVerifyImageData(vehicleImages.driving_license);
        setTruckLicense(truckLicense);
        setTruckDriveLicense(trcukDriveLicenseImage);
        setLoading(false);
      } catch (error) {
        console.error('获取图片失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [open, waybillId, truck, cargo, dataProvider]);

  const handleDownload = async () => {
    const allImages = receiptImage == null ? [idCardFrontImage, idCardBackImage, drivingLicenseImage, certificate, truckLicense, truckDriveLicense] : [idCardFrontImage, idCardBackImage, drivingLicenseImage, certificate, truckLicense, truckDriveLicense, receiptImage].filter(Boolean);
    if (allImages.length === 0) return;

    try {
      const zip = new JSZip();
      const currentDate = format(new Date(), 'yyyyMMdd_HHmmss');
      const zipFileName = `${cargo?.name ?? '未知货物'}_${truck?.plate ?? '未知车牌'}_${waybillId}_${currentDate}.zip`;

      // 添加图片到zip
      const imagePromises = allImages.map(async (imageUrl, index) => {
        const response = await fetch(imageUrl ?? "");
        const blob = await response.blob();
        const imageName = `${index + 1}_${getImageName(index)}.jpg`;
        zip.file(imageName, blob);
      });

      await Promise.all(imagePromises);

      // 生成并下载zip文件
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载图片失败:', error);
    }
  };

  // 获取图片名称的辅助函数
  const getImageName = (index: number): string => {
    switch (index) {
      case 0:
        return '身份证正面';
      case 1:
        return '身份证背面';
      case 2:
        return '驾驶证';
      case 3:
        return '道路运输证';
      case 4:
        return '从业资格证';
      case 5:
        return '行驶证';
      case 6:
        return '磅单';
      default:
        return '未知图片';
    }
  };

  return (
    <>
      {/* 图片列表 */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          {`${cargo?.name ?? '未知货物'} - ${truck?.plate ?? '未知车牌'} - ${waybillId}`}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {idCardFrontImage && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      身份证正面照
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={idCardFrontImage} alt="身份证正面照" />
                      <IconButton
                        onClick={() => setPreview({ src: idCardFrontImage, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}

              {idCardBackImage && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      身份证反面照
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={idCardBackImage} alt="身份证反面照" />
                      <IconButton
                        onClick={() => setPreview({ src: idCardBackImage, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}

              {drivingLicenseImage && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      驾驶证
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={drivingLicenseImage} alt="行驶证照片" />
                      <IconButton
                        onClick={() => setPreview({ src: drivingLicenseImage, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}

              {certificate && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      道路运输证
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={certificate} alt="道路运输证" />
                      <IconButton
                        onClick={() => setPreview({ src: certificate, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}

              {truckLicense && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      从业资格证
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={truckLicense} alt="货车营运证" />
                      <IconButton
                        onClick={() => setPreview({ src: truckLicense, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}
              {truckDriveLicense && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      行驶证
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 300,
                        position: 'relative',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          border: '1px solid #eee',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <img src={truckDriveLicense} alt="行驶证" />
                      <IconButton
                        onClick={() => setPreview({ src: truckDriveLicense, rotation: 0, scale: 1 })}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          bottom: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    磅单
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #eee',
                      borderRadius: 1,
                    }}
                  >
                    {receiptImage ? (
                      <>
                        <img
                          src={receiptImage}
                          alt="磅单"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                        <IconButton
                          onClick={() => setPreview({ src: receiptImage, rotation: 0, scale: 1 })}
                          sx={{
                            position: 'absolute',
                            right: 8,
                            bottom: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            },
                          }}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Typography color="textSecondary">
                        运单目前没有磅单照片
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>关闭</Button>
          <Button
            onClick={handleDownload}
            startIcon={<DownloadIcon />}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            下载全部
          </Button>
        </DialogActions>
      </Dialog>
      {/* 图片预览 */}
      <Dialog
        open={!!preview.src}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          {preview.src && (
            <>
              <img
                src={preview.src}
                alt="预览图"
                style={{
                  width: '100%',
                  height: '90vh',
                  objectFit: 'contain',
                  transform: `rotate(${preview.rotation}deg) scale(${preview.scale})`,
                  transition: 'transform 0.3s ease',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  display: 'flex',
                  gap: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <IconButton onClick={handleZoomIn} size="small" sx={{ color: 'white' }}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={handleZoomOut} size="small" sx={{ color: 'white' }}>
                  <ZoomOutIcon />
                </IconButton>
                <IconButton onClick={handleRotateLeft} size="small" sx={{ color: 'white' }}>
                  <RotateLeftIcon />
                </IconButton>
                <IconButton onClick={handleRotateRight} size="small" sx={{ color: 'white' }}>
                  <RotateRightIcon />
                </IconButton>
                <IconButton onClick={handleClosePreview} size="small" sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WaybillImagesDialog;